import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiEndpoint } from 'src/app/core/http/api';
import { LocaleService } from 'src/app/core/services/locale.service';
import { ReusableService } from 'src/app/services/reusable.service';
import { DsButtonComponent } from 'src/app/shared/components/ds-button/ds-button.component';
import { DsNumberInputComponent } from 'src/app/shared/components/ds-number-input/ds-number-input.component';
import { DsPopoverDirective } from 'src/app/shared/components/ds-popover/ds-popover.component';
import { DsSelectComponent } from 'src/app/shared/components/ds-select/ds-select.component';
import { DsTab, DsTabsComponent } from 'src/app/shared/components/ds-tabs/ds-tabs.component';
import { DsTooltipDirective } from 'src/app/shared/components/ds-tooltip/ds-tooltip.component';
import { IMarketSymbol } from 'src/app/shared/models/market-rate';
import {
  IFxBoard,
  buildFxBoard,
  convertibleCodes,
  crossRate,
  fxFractionDigits,
} from './fx-rates.util';

type FxTab = 'rates' | 'convert' | 'gold';

/** Nạp lại khi mở popover mà bảng đã cũ hơn mốc này. Job backend làm mới mỗi giờ. */
const RELOAD_AFTER_MS = 5 * 60 * 1000;

/** Giá cũ hơn mốc này thì báo — job chạy mỗi giờ, trễ 3 giờ nghĩa là job đang hỏng. */
const STALE_AFTER_MS = 3 * 60 * 60 * 1000;

const COMMON_PAIRS: [string, string][] = [
  ['USD', 'VND'],
  ['EUR', 'USD'],
  ['USD', 'JPY'],
  ['GBP', 'VND'],
  ['EUR', 'VND'],
  ['SGD', 'VND'],
];

/**
 * Nút "Tỷ giá & giá vàng" trên topbar — đối chiếu `FxButton` (module-rail.jsx) của bản thiết kế.
 *
 * Dữ liệu là danh mục `MarketSymbols` (realmarketapi.com), CHỈ ĐỌC: popover không gọi
 * `refresh-market-rates`, vì endpoint đó GHI `ExchangeRates` và tốn quota nhà cung cấp mỗi lần
 * mở. Giá do job backend làm mới mỗi giờ.
 *
 * Khác bản thiết kế, có chủ đích — thiết kế vẽ dữ liệu mà nguồn thật không có:
 * - Không cột "Mua TM" / "Mua CK": nguồn là giá bid/ask liên ngân hàng, không phải bảng giá
 *   của một ngân hàng. Bảng có Mua (bid) · Bán (ask) · Giữa.
 * - Không cột "24h": hệ thống chỉ giữ giá mới nhất của mỗi mã.
 * - Không CNY/KRW/THB: nhà cung cấp không có cặp nào cho ba tiền tệ này.
 * - Tab vàng là giá THẾ GIỚI (XAUUSD) quy đổi ra VND/lượng, không phải giá SJC/DOJI/PNJ.
 */
@Component({
  selector: 'app-fx-rates-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    RouterModule,
    TranslateModule,
    DsButtonComponent,
    DsNumberInputComponent,
    DsPopoverDirective,
    DsSelectComponent,
    DsTabsComponent,
    DsTooltipDirective,
  ],
  templateUrl: './fx-rates-button.component.html',
})
export class FxRatesButtonComponent {
  private readonly _reusable = inject(ReusableService);
  private readonly _locale = inject(LocaleService);
  private readonly _translate = inject(TranslateService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);

  readonly tabs: DsTab[] = [
    { value: 'rates', label: 'Layout.Fx.Tab.Rates' },
    { value: 'convert', label: 'Layout.Fx.Tab.Convert' },
    { value: 'gold', label: 'Layout.Fx.Tab.Gold' },
  ];

  open = false;
  activeTab: FxTab = 'rates';
  loading = false;
  loadError = false;
  board: IFxBoard = { rates: [], metals: [], quotedAt: null };
  codeOptions: { value: string; label: string }[] = [];

  amount: number | null = 1000;
  from = 'USD';
  to = 'VND';

  private _loadedAt = 0;

  onOpened(opened: boolean): void {
    this.open = opened;
    if (opened && (this.loadError || Date.now() - this._loadedAt > RELOAD_AFTER_MS)) {
      this.load();
    }
    this._cdr.markForCheck();
  }

  load(): void {
    this.loading = true;
    this.loadError = false;
    this._cdr.markForCheck();
    this._reusable
      .listTyped<IMarketSymbol>(ApiEndpoint.GetMarketSymbols, {
        // 61 mã là TOÀN BỘ danh mục nhà cung cấp — một trang 100 đọc hết.
        filterOptions: { pageIndex: 1, pageSize: 100 },
        isActive: true,
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (res) => {
          this.setBoard(buildFxBoard(res.result?.data ?? []));
          this._loadedAt = Date.now();
          this.loading = false;
          this._cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.loadError = true;
          this._cdr.markForCheck();
        },
      });
  }

  setBoard(board: IFxBoard): void {
    this.board = board;
    this.codeOptions = convertibleCodes(board).map((code) => ({ value: code, label: code }));
    const codes = this.codeOptions.map((o) => o.value);
    if (codes.length && !codes.includes(this.from)) this.from = codes[1] ?? codes[0];
    if (codes.length && !codes.includes(this.to)) this.to = codes[0];
  }

  onTabChange(tab: string): void {
    this.activeTab = tab as FxTab;
  }

  /** Bấm một dòng ở bảng tỷ giá → sang tab quy đổi với cặp ngoại tệ/VND, như thiết kế. */
  pickRate(code: string): void {
    this.from = code;
    this.to = 'VND';
    this.activeTab = 'convert';
  }

  pickPair(from: string, to: string): void {
    this.from = from;
    this.to = to;
  }

  setFrom(value: string | number): void {
    this.from = String(value);
  }

  setTo(value: string | number): void {
    this.to = String(value);
  }

  swap(): void {
    [this.from, this.to] = [this.to, this.from];
  }

  get rate(): number | null {
    return crossRate(this.board, this.from, this.to);
  }

  get result(): number | null {
    const rate = this.rate;
    return rate == null || this.amount == null ? null : this.amount * rate;
  }

  /** Cặp thường dùng — chỉ cặp mà cả hai chân đều có giá. */
  get commonPairs(): { from: string; to: string; rate: number }[] {
    return COMMON_PAIRS.map(([from, to]) => ({ from, to, rate: crossRate(this.board, from, to) }))
      .filter((p): p is { from: string; to: string; rate: number } => p.rate != null);
  }

  get isStale(): boolean {
    const at = this.board.quotedAt ? Date.parse(this.board.quotedAt) : NaN;
    return !Number.isNaN(at) && Date.now() - at > STALE_AFTER_MS;
  }

  get quotedAtLabel(): string {
    if (!this.board.quotedAt) return '';
    const at = new Date(this.board.quotedAt);
    if (Number.isNaN(at.getTime())) return '';
    // Ngày trước, giờ sau — như bản thiết kế; `Intl` tiếng Việt tự đặt giờ lên trước.
    const loc = this._locale.numberLocaleSignal();
    const date = new Intl.DateTimeFormat(loc, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(at);
    const time = new Intl.DateTimeFormat(loc, { hour: '2-digit', minute: '2-digit', hour12: false }).format(at);
    return `${date} ${time}`;
  }

  /** Tên ngoại tệ theo ngôn ngữ đang dùng — `Intl.DisplayNames` thay cho bảng khai tay. */
  currencyName(code: string): string {
    try {
      const lang = this._translate.currentLang || this._translate.getDefaultLang() || 'vi-VN';
      return new Intl.DisplayNames([lang], { type: 'currency' }).of(code) ?? code;
    } catch {
      return code;
    }
  }

  fmt(value: number | null | undefined, digits?: number): string {
    if (value == null || Number.isNaN(value)) return '—';
    const d = digits ?? fxFractionDigits(value);
    return this._locale.formatNumber(value, { minimumFractionDigits: d, maximumFractionDigits: d });
  }
}
